import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';

/** Wordmark scuro, da usare su sfondi chiari. */
export const LOGO_ON_LIGHT_SRC = '/wenkey%20transparant%20for%20white%20background.png';
/** Wordmark chiaro, da usare su sfondi scuri. */
export const LOGO_ON_DARK_SRC = '/wenkey%20transparant%20for%20black%20background.png';
/** Lockup orizzontale (nuvola + WENKEY + payoff), 500×200, sfondo trasparente. */
export const LOGO_EXTENDED_SRC = '/logoesteso.webp';

/** Natural size of both PNGs (1375×1320). */
const IMAGE_RATIO = 1375 / 1320;
/** Share of the file that is the mark plus “WENKEY”, without the tagline. */
const COMPACT_CLIP = 1236 / 1320;

export interface LogoProps {
  /** Rail mark: same lockup, tagline clipped so it fits the 68px column. */
  compact?: boolean;
  /** Horizontal lockup. Use only where the container is at least ~200px wide. */
  extended?: boolean;
  size?: 'small' | 'medium' | 'large';
  /** When set, the mark is a link (typically back to the public home). */
  to?: string;
}

export function Logo({ compact = false, extended = false, size = 'medium', to }: LogoProps): JSX.Element {
  const theme = useTheme();
  const src = extended ? LOGO_EXTENDED_SRC : theme.palette.mode === 'dark' ? LOGO_ON_DARK_SRC : LOGO_ON_LIGHT_SRC;
  const height = compact ? 52 : size === 'large' ? 168 : size === 'small' ? 56 : 96;

  const mark = extended ? (
    <Box
      component="img"
      src={src}
      alt=""
      aria-hidden="true"
      sx={{
        display: 'block',
        width: '100%',
        maxWidth: 240,
        height: 'auto',
        // The file is navy on transparent. Flip luminance and put the hue back
        // so the wordmark stays light on a dark surface and the cloud stays blue.
        ...(theme.palette.mode === 'dark' && { filter: 'invert(1) hue-rotate(180deg)' }),
      }}
    />
  ) : (
    <Box
      sx={{
        height: compact ? height * COMPACT_CLIP : height,
        width: height * IMAGE_RATIO,
        maxWidth: '100%',
        overflow: 'hidden',
        flexShrink: 0,
        lineHeight: 0,
      }}
    >
      <Box
        component="img"
        src={src}
        alt=""
        aria-hidden="true"
        sx={{
          display: 'block',
          width: '100%',
          height: 'auto',
        }}
      />
    </Box>
  );

  if (!to) return mark;

  return (
    <Box
      component={RouterLink}
      to={to}
      aria-label="Wenkey Hub, vai alla home"
      sx={{
        color: 'inherit',
        textDecoration: 'none',
        display: extended ? 'flex' : 'inline-flex',
        width: extended ? '100%' : undefined,
        maxWidth: extended ? 240 : undefined,
        minWidth: 0,
        lineHeight: 0,
      }}
    >
      {mark}
    </Box>
  );
}
